import React, { createContext, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isFeatureEnabled } from '../config/app'
import { useCustomAuth } from '../hooks/useCustomAuth'
import { useOAuthCallback } from '../hooks/useOAuthCallback'
import { fetchCurrentUser, selectUser } from '../store/slices/userSlice'
import { syncLocalWishlistToBackend } from '../store/slices/wishlistSlice'
import { AppDispatch } from '../store/store'
import { AuthFlowResult } from '../types/auth.js'
import { setAnalyticsIdentity, clearAnalyticsIdentity } from '../analytics'

interface AuthContextType {
  user: any | null
  isAuthenticated: boolean
  isLoading: boolean
  signOut: () => Promise<void>
  refreshAuth: () => Promise<void>
  // Auth methods
  customAuthInitiate: (username: string) => Promise<AuthFlowResult>
  customAuthChallenge: (
    session: string,
    deliveryMedium: 'email' | 'phone_number',
    username: string,
  ) => Promise<AuthFlowResult>
  customAuthOTP: (session: string, otpCode: string, username: string) => Promise<AuthFlowResult>
  otpSignUp: (username: string) => Promise<AuthFlowResult>
  otpConfirmSignUp: (
    username: string,
    confirmationCode: string,
    session: string,
    originalUsername: string,
  ) => Promise<AuthFlowResult>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    user,
    isAuthenticated,
    isLoading,
    signOut: customSignOut,
    refreshTokenAuth,
    customAuthInitiate,
    customAuthChallenge,
    customAuthOTP,
    otpSignUp,
    otpConfirmSignUp,
  } = useCustomAuth()

  const {
    isProcessing: isOAuthProcessing,
    isSuccess: isOAuthSuccess,
    error: oauthError,
    user: oauthUser,
    resetOAuthState,
  } = useOAuthCallback()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const shouldUseOAuth = isFeatureEnabled('enableCognitoGoogleOAuth')
  useEffect(() => {
    const refreshAuth = async () => {
      const storedRefreshToken = localStorage.getItem('refreshToken')
      const storedUsername = localStorage.getItem('username')

      if (storedRefreshToken && storedUsername && !isAuthenticated) {
        setIsRefreshing(true)
        try {
          const result = await refreshTokenAuth(storedUsername, storedRefreshToken)
          if (result.success) {
            localStorage.setItem('accessToken', result.data?.tokens?.accessToken || '')
            localStorage.setItem('refreshToken', result.data?.tokens?.refreshToken || '')
            localStorage.setItem('idToken', result.data?.tokens?.idToken || '')
          } else {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('idToken')
            localStorage.removeItem('username')
            // Clear user data from Redux store
            dispatch({ type: 'user/clearUserData' })
          }
        } catch (error) {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('idToken')
          localStorage.removeItem('username')
          // Clear user data from Redux store
          dispatch({ type: 'user/clearUserData' })
        } finally {
          setIsRefreshing(false)
        }
      }
    }

    refreshAuth()
  }, [refreshTokenAuth, isAuthenticated, dispatch])

  useEffect(() => {
    if (user && user.tokens) {
      localStorage.setItem('accessToken', user.tokens.accessToken || '')
      localStorage.setItem('refreshToken', user.tokens.refreshToken || '')
      localStorage.setItem('idToken', user.tokens.idToken || '')
      localStorage.setItem('username', user.username)
    }
  }, [user, dispatch])

  // Auto-fetch user data after successful authentication (custom or OAuth)
  useEffect(() => {
    const accessToken = user?.tokens?.accessToken || localStorage.getItem('accessToken')
    const idToken = user?.tokens?.idToken || localStorage.getItem('idToken')
    if (((shouldUseOAuth && isOAuthSuccess) || isAuthenticated) && (accessToken || idToken)) {
      dispatch(fetchCurrentUser())
      // Sync local wishlist items to backend when user logs in
      dispatch(syncLocalWishlistToBackend())
    }
  }, [
    isAuthenticated,
    isOAuthSuccess,
    shouldUseOAuth,
    user?.tokens?.accessToken,
    user?.tokens?.idToken,
    user?.username,
    dispatch,
  ])

  // Push the authenticated user's id/contacts into the analytics identity
  // registry so every event carries user.external_id / email / phone (raw —
  // backend hashes before egress to Meta CAPI / GA4 MP). When `reduxUser`
  // becomes null after signOut we drop back to the persistent anonymous id.
  const reduxUser = useSelector(selectUser)
  useEffect(() => {
    if (!reduxUser) {
      clearAnalyticsIdentity()
      return
    }
    setAnalyticsIdentity({
      external_id: reduxUser.id,
      email: reduxUser.contacts?.email ?? undefined,
      phone: reduxUser.contacts?.phone ?? undefined,
    })
  }, [reduxUser])

  const signOut = async () => {
    try {

      if (shouldUseOAuth && isFeatureEnabled('enableCognito')) {
        try {
          const { signOut: amplifySignOut } = await import('aws-amplify/auth')
          await amplifySignOut({ global: true })
        } catch (oauthError) {
          // OAuth signOut failed, falling back to local signOut
        }
      }

      if (isFeatureEnabled('enableCognito')) {
        try {
          const { authService } = await import('../services/cognito')
          await authService.signOut()
        } catch (cognitoError) {
          // Cognito authService signOut failed
        }
      }

      // Завжди викликаємо customSignOut для очищення локального стану
      await customSignOut()

      if (shouldUseOAuth) {
        resetOAuthState()
      }

    } catch (error) {
      // Навіть при помилці очищаємо локальний стан
      try {
        await customSignOut()
      } catch (cleanupError) {
        // Error during cleanup
      }

      if (shouldUseOAuth) {
        resetOAuthState()
      }
    }
  }

  const refreshAuth = async () => {
    const storedRefreshToken = localStorage.getItem('refreshToken')
    const storedUsername = localStorage.getItem('username')

    if (storedRefreshToken && storedUsername) {
      setIsRefreshing(true)
      try {
        const result = await refreshTokenAuth(storedUsername, storedRefreshToken)
        if (result.success) {
          localStorage.setItem('accessToken', result.data?.tokens?.accessToken || '')
          localStorage.setItem('refreshToken', result.data?.tokens?.refreshToken || '')
          localStorage.setItem('idToken', result.data?.tokens?.idToken || '')
        } else {
          await signOut()
        }
      } catch (error) {
        await signOut()
      } finally {
        setIsRefreshing(false)
      }
    }
  }

  const currentUser = shouldUseOAuth && oauthUser ? oauthUser : user
  const currentIsAuthenticated = (shouldUseOAuth && isOAuthSuccess) || isAuthenticated
  const currentIsLoading = isLoading || isRefreshing || (shouldUseOAuth && isOAuthProcessing)

  useEffect(() => {
    if (oauthError) {
      // OAuth authentication error
    }
  }, [oauthError])

  const value: AuthContextType = {
    user: currentUser,
    isAuthenticated: currentIsAuthenticated,
    isLoading: currentIsLoading,
    signOut,
    refreshAuth,
    // Auth methods
    customAuthInitiate,
    customAuthChallenge,
    customAuthOTP,
    otpSignUp,
    otpConfirmSignUp,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
